/**
 * deliveryPoint: the point every distance on the site is measured from, and
 * what the shopper is told about it.
 * ─────────────────────────────────────────────────────────────────────────────
 * The site measures from the browse location the shopper set (redux
 * `userLocation`: a typed address, a GPS fix or an IP guess, kept up to 24 h).
 * A distance that does not say where it is measured from reads as "from where
 * I am standing", which a location set this morning at home is not. So the
 * point carries the place it names, and the storefront picker says
 * "1.2 km from Bulenga, Wakiso" (4 Oct 2026, after Izi saw a "nearest" branch
 * in the app that was nearest to his saved address, not to him).
 *
 * The apps' twin is Lusuku_Expo_Clean app/utility/deliveryPoint.js.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// "Bulenga, Wakiso, Uganda" reads as "Bulenga, Wakiso": the shopper knows the
// country, and the space goes to the part that tells places apart.
const COUNTRY_SUFFIX = /,\s*(Uganda|Zambia)\s*$/i;

/** A place description without the trailing country. */
export const shortPlace = (text) =>
  (typeof text === "string" ? text : "")
    .trim()
    .replace(COUNTRY_SUFFIX, "")
    .trim();

/**
 * The point with its label, from coordinates already checked against the
 * market (hooks/useDeliveryPoint does that).
 *
 * @param {object} args
 * @param {number} args.latitude
 * @param {number} args.longitude
 * @param {string} [args.address]  the place the shopper set, as text
 * @param {string} [args.source]   userLocation.locationSource: gps | ip | manual
 * @returns {{ latitude:number, longitude:number, label:string, place:string,
 *             source:string }}
 */
export const deliveryPointOf = ({ latitude, longitude, address, source }) => {
  const place = shortPlace(address);
  return {
    latitude,
    longitude,
    place,
    source: source || "manual",
    label: place || (source === "gps" ? "Current location" : "Your location"),
  };
};

/**
 * The point inside a sentence: "1.2 km from Bulenga, Wakiso". A GPS fix with
 * no address reads "your current location". Empty when there is no point.
 */
export const deliveryPointPhrase = (point) => {
  if (!point) return "";
  if (point.place) return point.place;
  return point.source === "gps" ? "your current location" : "your location";
};
